import {
  addMonths,
  endOfDay,
  endOfMonth,
  format,
  isSameMonth,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export interface PeriodSelection {
  type: 'current' | 'month' | 'custom';
  startDate: Date;
  endDate: Date;
  label: string;
}

interface PeriodSelectorProps {
  selectedPeriod: PeriodSelection;
  onPeriodChange: (period: PeriodSelection) => void;
}

export function PeriodSelector({
  selectedPeriod,
  onPeriodChange,
}: PeriodSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();
  const today = new Date();

  const createMonthPeriod = (date: Date): PeriodSelection => ({
    type: 'month',
    startDate: startOfMonth(date),
    endDate: endOfMonth(date),
    label: format(date, 'MMMM yyyy', { locale: ptBR }),
  });

  const createCustomPeriod = (
    startDate: Date,
    endDate: Date
  ): PeriodSelection => ({
    type: 'custom',
    startDate: startOfDay(startDate),
    endDate: endOfDay(endDate),
    label: `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`,
  });

  const getCurrentPeriod = (): PeriodSelection => ({
    type: 'current',
    startDate: startOfMonth(today),
    endDate: endOfMonth(today),
    label: 'Mês atual',
  });

  const handlePreviousMonth = () => {
    if (selectedPeriod.type === 'current') {
      const lastMonth = subMonths(today, 1);
      onPeriodChange(createMonthPeriod(lastMonth));
    } else {
      const previousMonth = subMonths(selectedPeriod.startDate, 1);
      onPeriodChange(createMonthPeriod(previousMonth));
    }
  };

  const handleNextMonth = () => {
    if (selectedPeriod.type === 'current') return; // Can't go to future from current

    const nextMonth = addMonths(selectedPeriod.startDate, 1);

    // If next month is current month, switch to "current" mode
    if (isSameMonth(nextMonth, today)) {
      onPeriodChange(getCurrentPeriod());
    } else {
      onPeriodChange(createMonthPeriod(nextMonth));
    }
  };

  const handleResetToCurrent = () => {
    onPeriodChange(getCurrentPeriod());
  };

  const handleApplyCustomPeriod = () => {
    if (customStartDate && customEndDate) {
      onPeriodChange(createCustomPeriod(customStartDate, customEndDate));
      setIsOpen(false);
      setCustomStartDate(undefined);
      setCustomEndDate(undefined);
    }
  };

  const handleQuickPeriod = (monthsBack: number) => {
    if (monthsBack === 0) {
      onPeriodChange(getCurrentPeriod());
    } else {
      const targetDate = subMonths(today, monthsBack);
      onPeriodChange(createMonthPeriod(targetDate));
    }
    setIsOpen(false);
  };

  const isCurrentMonth =
    selectedPeriod.type === 'current' ||
    isSameMonth(selectedPeriod.startDate, today);
  const canGoNext = !isCurrentMonth;

  const quickOptions = [
    { label: 'Mês atual', value: 0 },
    { label: 'Mês passado', value: 1 },
    { label: 'Há 2 meses', value: 2 },
    { label: 'Há 3 meses', value: 3 },
    { label: 'Há 6 meses', value: 6 },
    { label: 'Há 1 ano', value: 12 },
  ];

  return (
    <div className="flex items-center gap-2">
      {/* Navigation buttons */}
      <div className="flex items-center gap-1">
        <Button
          className="h-8 w-8 p-0"
          onClick={handlePreviousMonth}
          size="sm"
          variant="outline"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Button
          className="h-8 w-8 p-0"
          disabled={!canGoNext}
          onClick={handleNextMonth}
          size="sm"
          variant="outline"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Period display and selector */}
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <PopoverTrigger asChild>
          <Button className="gap-2" variant="outline">
            <CalendarIcon className="h-4 w-4" />
            <span className="capitalize">{selectedPeriod.label}</span>
            {!isCurrentMonth && (
              <Badge className="text-xs" variant="secondary">
                Histórico
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Tabs className="w-auto" defaultValue="quick">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="quick">Rápido</TabsTrigger>
              <TabsTrigger value="month">Mês</TabsTrigger>
              <TabsTrigger value="custom">Período</TabsTrigger>
            </TabsList>

            <TabsContent className="space-y-2 p-4" value="quick">
              <h4 className="font-medium text-sm">Períodos rápidos</h4>
              <div className="grid gap-1">
                {quickOptions.map((option) => (
                  <Button
                    className="h-8 justify-start"
                    key={option.value}
                    onClick={() => handleQuickPeriod(option.value)}
                    size="sm"
                    variant={
                      (option.value === 0 &&
                        selectedPeriod.type === 'current') ||
                      (option.value > 0 &&
                        selectedPeriod.type === 'month' &&
                        isSameMonth(
                          selectedPeriod.startDate,
                          subMonths(today, option.value)
                        ))
                        ? 'default'
                        : 'ghost'
                    }
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </TabsContent>

            <TabsContent className="space-y-2 p-4" value="month">
              <h4 className="font-medium text-sm">Escolher mês específico</h4>
              <Calendar
                disabled={(date) => date > today}
                initialFocus
                mode="single"
                onSelect={(date) => {
                  if (date) {
                    if (isSameMonth(date, today)) {
                      onPeriodChange(getCurrentPeriod());
                    } else {
                      onPeriodChange(createMonthPeriod(date));
                    }
                    setIsOpen(false);
                  }
                }}
                selected={selectedPeriod.startDate}
              />
            </TabsContent>

            <TabsContent className="space-y-4 p-4" value="custom">
              <h4 className="font-medium text-sm">Período personalizado</h4>
              <p className="mb-3 text-muted-foreground text-xs">
                Clique na data inicial, depois na data final
              </p>

              <Calendar
                disabled={(date) => date > today}
                initialFocus
                mode="range"
                numberOfMonths={2}
                onSelect={(range) => {
                  setCustomStartDate(range?.from);
                  setCustomEndDate(range?.to);
                }}
                selected={{
                  from: customStartDate,
                  to: customEndDate,
                }}
              />

              {customStartDate && customEndDate && (
                <div className="border-t pt-2">
                  <div className="mb-2 rounded bg-muted p-2 text-center text-sm">
                    <strong>Período selecionado:</strong>
                    <br />
                    {format(customStartDate, 'dd/MM/yyyy')} até{' '}
                    {format(customEndDate, 'dd/MM/yyyy')}
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleApplyCustomPeriod}
                    size="sm"
                  >
                    Aplicar período
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>

      {/* Reset to current button */}
      {!isCurrentMonth && (
        <Button
          className="gap-2"
          onClick={handleResetToCurrent}
          size="sm"
          variant="outline"
        >
          <RotateCcw className="h-4 w-4" />
          Atual
        </Button>
      )}
    </div>
  );
}
